import { fetchJson } from "@/services/api-client";
import { SubscriptionCustomer } from "@/types/subscription";

/**
 * Creates a Stripe customer
 * @param organizationId The organization ID
 * @param customerPayload The customer payload to create
 */
export async function createCustomer(
  organizationId: string,
  customerPayload: SubscriptionCustomer,
) {
  const { customerId } = await fetchJson<{ customerId: string }>(
    "/api/stripe/customers",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        organizationId,
        customerPayload,
      }),
    },
    { fallbackErrorMessage: "Failed to create Stripe customer" },
  );

  return customerId;
}

/**
 * Gets the customer from the checkout session ID
 * @param sessionId The checkout session ID from the payment link
 */
export async function getCustomerFromSessionID(
  organizationId: string,
  sessionId: string,
): Promise<any> {
  const { customer } = await fetchJson<{ customer: any }>(
    `/api/stripe/sessions?sessionId=${encodeURIComponent(sessionId)}&orgId=${encodeURIComponent(organizationId)}`,
    {
      method: "GET",
    },
    { fallbackErrorMessage: "Failed to fetch customer from session ID" },
  );

  return customer;
}

/**
 * Matches an existing Stripe customer (searched server-side by the caller's
 * email) to the organization. On an exact single match the server writes the
 * customerId onto the org (`update_organization` as the caller — see
 * /api/stripe/customers/match); zero/multiple matches write nothing and the
 * returned count describes customer matches. activeProductIds lists active
 * subscriptions successfully synced, or is null when sync could not complete.
 * @param organizationId The organization ID
 */
export async function matchCustomerToOrganization(
  organizationId: string,
): Promise<{
  customerId: string | null;
  count: number;
  activeProductIds?: string[] | null;
}> {
  return fetchJson<{
    customerId: string | null;
    count: number;
    activeProductIds?: string[] | null;
  }>(
    "/api/stripe/customers/match",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ organizationId }),
    },
    { fallbackErrorMessage: "Failed to match Stripe customer" },
  );
}

/**
 * Searches for a customer by ownerId
 * @param organizationId The organization ID
 * @param ownerId The owner ID of the organization
 */
export async function searchCustomerByOwnerEmail(
  organizationId: string,
  ownerId: string,
): Promise<any[]> {
  const { data } = await fetchJson<{ data: any[] }>(
    `/api/stripe/customers?orgId=${encodeURIComponent(organizationId)}&ownerId=${encodeURIComponent(ownerId)}`,
    {
      method: "GET",
    },
    { fallbackErrorMessage: "Failed to search customer by ownerId" },
  );

  return data;
}
